import React from 'react'
import { Button, CircularProgress } from '@material-ui/core'
import DOMPurify from 'dompurify'

import { snackbar } from 'components'
import { ERRORS } from 'consts'
import { useApi, useApiCall } from 'hooks'

import { DocumentEditor, normalizeEditorHtml } from '../components'
import { getLegalTexts, updateLegalTexts } from './api'
import { useStyles } from './styles'
import { GetLegalTextsResponse, LegalTexts, UpdateLegalTextsPayload } from './types'

const emptyLegalTexts: LegalTexts = {
  termsHtml: '',
  privacyHtml: '',
}

const LegalTab: React.FC = () => {
  const classes = useStyles()

  const [{ data, isLoading }, , setData] = useApi<GetLegalTextsResponse, LegalTexts>(
    getLegalTexts,
    response => response.legal,
    {
      baseData: emptyLegalTexts,
      onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE),
    },
  )
  const [saveLegalTexts, isSaving] = useApiCall<UpdateLegalTextsPayload, GetLegalTextsResponse>(
    updateLegalTexts,
  )

  const handleSave = async () => {
    try {
      const { legal } = await saveLegalTexts({
        termsHtml: DOMPurify.sanitize(normalizeEditorHtml(data.termsHtml)),
        privacyHtml: DOMPurify.sanitize(normalizeEditorHtml(data.privacyHtml)),
      })
      setData(legal)
      snackbar.show('Textos legales guardados.')
    } catch {
      snackbar.show('No se pudieron guardar los textos legales. Intente de nuevo.')
    }
  }

  if (isLoading) {
    return (
      <div className={classes.loader}>
        <CircularProgress color="primary" />
      </div>
    )
  }

  return (
    <>
      <div className={classes.pageBody}>
        <div className={classes.editorSection}>
          <DocumentEditor
            label="Términos y condiciones"
            value={data.termsHtml}
            onChange={termsHtml =>
              setData(current =>
                current.termsHtml === termsHtml ? current : { ...current, termsHtml },
              )
            }
            readOnly={isSaving}
          />
        </div>
        <div className={classes.editorSection}>
          <DocumentEditor
            label="Política de privacidad"
            value={data.privacyHtml}
            onChange={privacyHtml =>
              setData(current =>
                current.privacyHtml === privacyHtml ? current : { ...current, privacyHtml },
              )
            }
            readOnly={isSaving}
          />
        </div>
      </div>
      <div className={classes.pageActions}>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={isLoading || isSaving}
        >
          Guardar cambios
        </Button>
      </div>
    </>
  )
}

export { LegalTab }
